import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  TablePaginatedV8,
  TableCellV8,
  EditCellV8,
  TableEditButtons,
  promiseModalDialog,
  toastSuccess,
} from 'motion-components';
import { useFormikContext } from 'formik';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useMutation } from '@apollo/client';
import { DELETE_CONTAINER_ORDER_LINE } from '../graphql';
import { useGetContainerTypes } from 'shared-pages/components/field-select/fs-queries';
import { gridInactiveRowStyle, gridRowSelectedStyle } from 'shared-pages/common/grid-helpfunc';
import { isAfisCyprus } from 'shared-pages/common/constants';

interface ContainerOrderLine {
  id: number | null;
  aa: number | null;
  containerTypeId: number | null;
  quantity: number | null;
  notes?: string;
}

export const ContainerOrderLinesTable = ({
  lines,
  canEdit,
  canInsert,
}: {
  lines: ContainerOrderLine[];
  canEdit: boolean;
  canInsert: boolean;
}) => {
  const { t } = useTranslation();
  const gridRef = useRef(null);
  const { appStore, tableStore } = useRootStore();
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [deleteLine] = useMutation(DELETE_CONTAINER_ORDER_LINE);
  const { values, setFieldValue, errors } = useFormikContext<any>();

  const columns = useMemo(() => {
    return [
      {
        id: 'aa',
        header: t('Αα'),
        columnName: t('Αα'),
        accessorKey: 'aa',
        orderByField: 'aa',
        cell: TableCellV8,

        meta: { type: 'number' },
        size: 5,
      },
      {
        id: 'containerTypeId',
        header: t('Τύπος Συσκευασίας'),
        accessorKey: 'containerTypeId',
        columnName: t('Τύπος Συσκευασίας'),
        enableSorting: false,
        cell: TableCellV8, // Could be a custom select cell in advanced usage
        size: 80,
        meta: {
          type: 'select',
          idKey: 'containerTypeId', // The key for the option's unique ID
          idName: 'containertype.name', // The key for the option's display text
          hook: useGetContainerTypes,
        },
      },
      {
        id: 'quantity',
        header: t('Ποσότητα'),
        accessorKey: 'quantity',
        columnName: t('Ποσότητα'),
        enableSorting: false,
        cell: TableCellV8,
        size: 50,
        meta: {
          type: 'number',
        },
      },
      {
        id: 'notes',
        header: t('Σημειώσεις'),
        columnName: t('Σημειώσεις'),
        accessorKey: 'notes',
        enableSorting: false,
        orderByField: 'notes',
        cell: TableCellV8,
        meta: {
          type: 'text',
          nullable: true,
        },
        size: 50,
      },
      ...(isAfisCyprus
        ? [
            {
              id: 'binSerialNo',
              header: t('Αριθμός Κάδου'),
              columnName: t('Αριθμός Κάδου'),
              accessorKey: 'binSerialNo',
              orderByField: 'binSerialNo',
              enableSorting: false,
              cell: TableCellV8,
              meta: {
                type: 'text',
                nullable: true,
              },
              size: 50,
            },
          ]
        : []),
      {
        id: 'edit',
        header: t('Ενέργειες'),
        columnName: t('Ενέργειες'),
        enableSorting: false,
        accessorKey: 'edit',
        size: 120,
        cell: (props: any) => {
          const { row, table } = props;
          return canEdit ? EditCellV8({ row, table }) : <></>;
        },
      },
    ];
  }, [t]);

  const onSaveRow = useCallback(
    async (
      rowIndex: number,
      rowOriginal: ContainerOrderLine,
      changes: Partial<ContainerOrderLine>,
    ) => {
      setLoading(true);
      try {
        const updatedLines = [...lines];
        updatedLines[rowIndex] = { ...rowOriginal, ...changes };
        setFieldValue('lines', updatedLines);
        return true;
      } catch (err) {
        console.error('Error saving row:', err);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [lines, setFieldValue],
  );

  const handleRemoveLine = (rowIndex: number) => {
    const newLines = [...lines];
    newLines.splice(rowIndex, 1);
    setFieldValue('lines', newLines);
  };

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      <div
        className="form-group-title d-flex justify-content-between align-items-center overflow-y-hidden"
        style={{ marginBottom: '0px' }}
      >
        <h5 style={{ marginBottom: '0px' }}>{t('Τύποι Συσκευασιών')}</h5>
        <div className="d-flex">
          <TableEditButtons
            className="me-3"
            canEdit={canEdit}
            canInsert={canInsert}
            canDelete={canEdit}
            tableOptionsMeta={() => {
              if (gridRef.current) return (gridRef.current as any).table.options.meta;
              else return undefined;
            }}
            twoLines={false}
          />
        </div>
      </div>
      <TablePaginatedV8
        id="container-order-lines"
        style={{ overflow: 'visible' }}
        loading={false}
        appStore={appStore}
        tableStore={tableStore}
        columns={columns as any}
        data={lines}
        ref={gridRef}
        onRowClick={(table) => setSelected(table.row.original)}
        rowProps={(row: any) => {
          const isRowNew = row.original.id === null;
          const rowHasError = errors.containerOrderLines?.[row.index];
          let style = isRowNew ? { color: '#4987b7' } : {};
          if (row.original.id === selected?.id) style = { ...style, ...gridRowSelectedStyle };
          if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
          if (rowHasError) style = { ...style, color: 'red' };
          return { style };
        }}
        deleteRow={async (row) => {
          if (!row || !row.id) return false;
          const confirm = await promiseModalDialog({
            title: 'Διαγραφή Συσκευασίας',
            text: `<div>Θέλετε να διαγράψετε την Συσκευασία <strong>${
              row?.containertype?.name ?? ''
            }</strong>;</div>`,
          });
          if (!confirm) return false;
          const res = await deleteLine({ variables: { id: row?.id } });
          if (!res) return false;
          toastSuccess('Διαγράφηκε η Συσκευασία');
          handleRemoveLine(row.index);
          return true;
        }}
        onNewRow={() => ({
          id: null,
          aa: lines.length + 1,
          notes: '',
          containerTypeId: null,
          quantity: 1,
          containertype: { id: null, name: '' },
        })}
        dataCount={lines.length}
        topBorderRadius={false}
        onSaveRow={onSaveRow}
        initSorting={[{ id: 'aa', desc: false }]}
      />
      {errors && errors?.lines && (
        <div>
          {/* Display a general error message */}
          <small style={{ color: 'red' }}>
            {Array.isArray(errors.lines)
              ? 'Υπάρχουν λάθη στις γραμμές του πίνακα'
              : String(errors.lines)}
          </small>

          {/* Check if there are multiple errors and map through them */}
          {Array.isArray(errors?.lines) && errors?.lines.length > 0 && (
            <ul style={{ color: 'red', marginTop: '10px' }}>
              {errors.lines.map(
                (error, index) =>
                  errors.lines[index] && (
                    <li key={index}>
                      <strong>
                        {t('Σειρά')} {index + 1}:
                      </strong>
                      {error && typeof error === 'object'
                        ? Object.entries(error)
                            .map(([field, message]) => `${message}`)
                            .join(', ')
                        : ''}
                    </li>
                  ),
              )}
            </ul>
          )}
        </div>
      )}
    </LoadingOverlay>
  );
};
